// Production Dashboard - Refactored with onepage_dashboard pattern
import React, { useState, useEffect } from 'react';
import {
    Box,
    Text,
    useToast,
    useColorModeValue,
    Container,
    Heading,
    useBreakpointValue,
} from '@chakra-ui/react';
import api from '../auth/apiClient';

// Import individual production components
import LHPProduksiAkumulasi from './lhp/LHPProduksiAkumulasi';
import LHPProduksiHarian from './lhp/LHPProduksiHarian';
import CPOProduksiAkumulasi from './cpo/CpoProduksiAkumulasi';
import KernelProduksiAkumulasi from './kernel/KernelProduksiAkumulasi';
import CangkangProduksiAkumulasi from './cangkang/CangkangProduksiAkumulasi';
import CPOProduksiHarian from './cpo/CpoTrendHarian';
import KernelTrendHarian from './kernel/KernelTrendHarian';
import CangkangTrendHarian from './cangkang/CangkangTrendHarian';

// Import new consistent components
import ProductionSection from './produksi/ProductionSection';
import FilterSection from './produksi/FilterSection';
import LoadingOverlay from './produksi/LoadingOverlay';
import LoadingSkeleton from './produksi/LoadingSkeleton';

// Import types
import type {
    FilterState,
    LHPTrendDatum,
    LHPHarianDatum,
    CpoTrendDatum,
    KernelTrendDatum,
    CangkangTrendDatum,
    CPOHarianDatum,
    KernelHarianDatum,
    CangkangHarianDatum,
} from './produksi/types';
import LHPProduksiTrend from './lhp/LHPProduksiTrend';
import CpoTrend from './cpo/CpoTrend';
import KernelTrend from './kernel/KernelTrend';
import CangkangTrend from './cangkang/CangkangTrend';

const ProduksiDashboard = () => {
    // State management
    const [filters, setFilters] = useState<FilterState>({
        tahun_bulan: `${new Date().getFullYear()}${(new Date().getMonth() + 1).toString().padStart(2, '0')}`,
        jenis_laporan: "aktual_vs_target_ytd",
    });
    const [appliedFilters, setAppliedFilters] = useState<FilterState>(filters);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Toast for notifications
    const toast = useToast();
    
    // Color mode values
    const bgColor = useColorModeValue('gray.50', 'gray.900');
    const textColor = useColorModeValue('gray.800', 'white');

    // Data states
    const [dataLHP, setDataLHP] = useState<LHPTrendDatum[]>([]);
    const [dataLHPHarian, setDataLHPHarian] = useState<LHPHarianDatum[]>([]);
    const [dataCPO, setDataCPO] = useState<CpoTrendDatum[]>([]);
    const [dataKernel, setDataKernel] = useState<KernelTrendDatum[]>([]);
    const [dataCangkang, setDataCangkang] = useState<CangkangTrendDatum[]>([]);
    const [dataCPOHarian, setDataCPOHarian] = useState<CPOHarianDatum[]>([]);
    const [dataKernelHarian, setDataKernelHarian] = useState<KernelHarianDatum[]>([]);
    const [dataCangkangHarian, setDataCangkangHarian] = useState<CangkangHarianDatum[]>([]);

    const ESTATE_OPTIONS = [
        "ESTATE LEMATANG MAKMUR",
        "ESTATE MUSI MAKMUR",
        "PLASMA ABAB MAKMUR 1",
        "PLASMA ABAB MAKMUR 2"
    ];
    
    const fetchLHPData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const year = filters.tahun_bulan.slice(0, 4);
            const response = await api.get(`/api/report-lhp?start_period=${year}`);
            setDataLHP(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data produksi');
            toast({ 
                title: 'Error', 
                description: err.message || 'Terjadi kesalahan',
                status: 'error', 
                duration: 3000, 
                isClosable: true 
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchLHPHarian = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/api/report-lhp-tbs-harian?start_period=${filters.tahun_bulan}`);
            if (response.data.status !== 'success') throw new Error(response.data.message);
            setDataLHPHarian(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data harian');
            toast({
                title: 'Error',
                description: err.message || 'Terjadi kesalahan',
                status: 'error',
                duration: 3000,
                isClosable: true,
        });
        } finally {
        setLoading(false);
        }
    };

    const fetchCPOData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const year = filters.tahun_bulan.slice(0, 4);
            const response = await api.get(`/api/report-cpo?periode=${year}`);
            setDataCPO(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data produksi');
            toast({ 
                title: 'Error', 
                description: err.message || 'Terjadi kesalahan',
                status: 'error', 
                duration: 3000, 
                isClosable: true 
            });
        } finally {
            setLoading(false);
        }
    };
    
    const fetchCPOHarian = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/api/report-cpo-daily?periode=${filters.tahun_bulan}`);
            if (response.data.status !== 'success') throw new Error(response.data.message);
            setDataCPOHarian(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data harian');
            toast({
                title: 'Error',
                description: err.message || 'Terjadi kesalahan',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    
    const fetchKernelData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const year = filters.tahun_bulan.slice(0, 4);
            const response = await api.get(`/api/report-kernel?periode=${year}`);
            setDataKernel(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data produksi');
            toast({ 
                title: 'Error', 
                description: err.message || 'Terjadi kesalahan',
                status: 'error', 
                duration: 3000, 
                isClosable: true 
            });
        } finally {
            setLoading(false);
        }
    };
    
    const fetchKernelHarian = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/api/report-kernel-daily?periode=${filters.tahun_bulan}`);
            if (response.data.status !== 'success') throw new Error(response.data.message);
            setDataKernelHarian(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data harian');
            toast({
                title: 'Error',
                description: err.message || 'Terjadi kesalahan',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };
    const fetchCangkangData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const year = filters.tahun_bulan.slice(0, 4);
            const response = await api.get(`/api/report-cangkang?periode=${year}`);
            setDataCangkang(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data produksi');
            toast({ 
                title: 'Error', 
                description: err.message || 'Terjadi kesalahan',
                status: 'error', 
                duration: 3000, 
                isClosable: true 
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchCangkangHarian = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await api.get(`/api/report-cangkang-daily?periode=${filters.tahun_bulan}`);
            if (response.data.status !== 'success') throw new Error(response.data.message);
            setDataCangkangHarian(Array.isArray(response.data.data) ? response.data.data : []);
        } catch (err: any) {
            setError('Gagal memuat data harian');
            toast({
                title: 'Error',
                description: err.message || 'Terjadi kesalahan',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchData = async () => {
        await fetchLHPData();
        await fetchLHPHarian();
        await fetchCPOData();
        await fetchCPOHarian();
        await fetchKernelData();
        await fetchKernelHarian();
        await fetchCangkangData();
        await fetchCangkangHarian();
    };

    useEffect(() => {
        fetchLHPData();
        fetchLHPHarian();
        fetchCPOData();
        fetchCPOHarian();
        fetchKernelData();
        fetchKernelHarian();
        fetchCangkangData();
        fetchCangkangHarian();
    }, []);

    const handleFiltersChange = (newFilters: FilterState) => {
        setFilters(newFilters);
    };

    const handleApplyFilters = () => {
        setAppliedFilters(filters);
        fetchData();
    };

    console.log('Filters:', filters);

    const dashboardHeight = useBreakpointValue({ 
        base: 'auto', 
        md: 'calc(100vh - 80px)', // Laptop
        lg: 'calc(100vh - 90px)',  // Monitor
        xl: '100vh',               // TV/Large monitors
        '2xl': '100vh'
      });

    return (
        <Box minH={dashboardHeight} bg="gray.100" p={2} w="100%">
            <Container bg="gray.100" maxW="100%" minH={dashboardHeight} p={0}>
                <Box minH="100vh" w="100%">
                    {/* Loading Overlay */}
                    <LoadingOverlay isVisible={loading} />
                        {/* <Heading 
                            size="lg" 
                            mb={6} 
                            color={textColor}
                            fontWeight="bold"
                        >
                            Dashboard Mill
                        </Heading> */}

                        {/* Filter Section */}
                        <FilterSection
                            filters={filters}
                            onFiltersChange={handleFiltersChange}
                            onApplyFilters={handleApplyFilters}
                            isLoading={loading}
                        />

                        {/* Content */}
                        {loading ? (
                            <LoadingSkeleton sectionsCount={4} />
                        ) : (
                            <>
                                {/* LHP Section */}
                                <ProductionSection
                                    // title="Laporan Harian Produksi (LHP)"
                                    akumulasiComponent={
                                        <LHPProduksiTrend 
                                            data={(dataLHP ?? []).filter(d => d.periode <= appliedFilters.tahun_bulan)} 
                                        />
                                    }
                                    harianComponent={
                                        <LHPProduksiHarian data={dataLHPHarian} />
                                    }
                                />

                                {/* CPO Section */}
                                <ProductionSection
                                    // title="Crude Palm Oil (CPO)"
                                    akumulasiComponent={
                                        <CpoTrend 
                                            data={(dataCPO ?? []).filter(d => d.periode <= appliedFilters.tahun_bulan)} 
                                        />
                                    }
                                    harianComponent={
                                        <CPOProduksiHarian data={dataCPOHarian} />
                                    }
                                />

                                {/* Kernel Section */}
                                <ProductionSection
                                    // title="Palm Kernel (PK)"
                                    akumulasiComponent={
                                        <KernelTrend 
                                            data={(dataKernel ?? []).filter(d => d.periode <= appliedFilters.tahun_bulan)} 
                                        />
                                    }
                                    harianComponent={
                                        <KernelTrendHarian data={dataKernelHarian} />
                                    }
                                />

                                {/* Cangkang Section */}
                                <ProductionSection
                                    // title="Cangkang Kelapa Sawit"
                                    akumulasiComponent={
                                        <CangkangTrend 
                                            data={(dataCangkang ?? []).filter(d => d.periode <= appliedFilters.tahun_bulan)} 
                                        />
                                    }
                                    harianComponent={
                                        <CangkangTrendHarian data={dataCangkangHarian} />
                                    }
                                />
                            </>
                        )}
                </Box>
            </Container>
        </Box>
    );
};

export default ProduksiDashboard;